import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { LayoutService } from 'app/features/layout/services/layout.service';

const PANEL_MIN = 180;
const PANEL_MAX_CAP = 640;
const MAIN_MIN = 320;

@Component({
  selector: 'app-content-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './content-shell.component.html',
  styleUrl: './content-shell.component.scss',
})
export class ContentShellComponent implements OnInit {
  /** Current left panel width in px (writable for drag resize and optional `[(panelWidth)]`). */
  panelWidth = model(260);
  collapsed = model(false);

  /**
   * When true, an invisible drag strip on the panel’s right edge resizes the aside.
   * Off by default so list-heavy shells (Sources, Projects, …) keep full clickable width.
   */
  resizablePanel = input(false);

  protected readonly panelResizing = signal(false);

  private readonly layoutService = inject(LayoutService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  ngOnInit(): void {
    this.layoutService.drawerToggle$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toggle());
  }

  toggle(): void {
    this.collapsed.update(v => !v);
  }

  protected onPanelResizePointerDown(event: PointerEvent): void {
    if (!this.resizablePanel() || this.collapsed()) {
      return;
    }
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);

    const startX = event.clientX;
    const startWidth = this.panelWidth();
    const maxW = this.maxPanelWidth();

    this.panelResizing.set(true);
    const body = this.document.body;
    const prevCursor = body.style.cursor;
    const prevUserSelect = body.style.userSelect;
    body.style.cursor = 'ew-resize';
    body.style.userSelect = 'none';

    const clamp = (w: number): number =>
      Math.round(Math.min(maxW, Math.max(PANEL_MIN, w)));

    const onMove = (e: PointerEvent): void => {
      const delta = e.clientX - startX;
      this.panelWidth.set(clamp(startWidth + delta));
    };

    const onEnd = (e: PointerEvent): void => {
      if (handle.hasPointerCapture(e.pointerId)) {
        handle.releasePointerCapture(e.pointerId);
      }
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onEnd);
      handle.removeEventListener('pointercancel', onEnd);
      this.panelResizing.set(false);
      body.style.cursor = prevCursor;
      body.style.userSelect = prevUserSelect;
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd);
    handle.addEventListener('pointercancel', onEnd);
  }

  private maxPanelWidth(): number {
    const vw =
      typeof this.document.defaultView !== 'undefined' && this.document.defaultView
        ? this.document.defaultView.innerWidth
        : 1200;
    return Math.max(PANEL_MIN + 40, Math.min(PANEL_MAX_CAP, vw - MAIN_MIN));
  }
}
